import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { 
  User, Building2, BookOpen, MapPin, ClipboardCheck, GraduationCap,
  ArrowRight
} from 'lucide-react';

const portals = [
  {
    id: 'candidate',
    title: 'Candidate Portal',
    description: 'For medical practitioners applying for certification examinations',
    icon: User,
    href: '/login',
    color: 'bg-blue-500',
    bgLight: 'bg-blue-50',
    features: ['Apply for examinations', 'Take MCQ tests', 'Submit case logs', 'View certificates']
  },
  {
    id: 'examiner',
    title: 'Examiner Portal',
    description: 'For IMCAC-appointed examiners to assess candidates',
    icon: ClipboardCheck,
    href: '/login/examiner',
    color: 'bg-[#1B2A4A]',
    bgLight: 'bg-slate-50',
    features: ['Grade candidate assessments', 'Score OSCE & Viva', 'Review case logs', 'Submit final scores']
  },
  {
    id: 'institution',
    title: 'Institution Accreditation',
    description: 'For training institutions seeking IMCAC accreditation',
    icon: Building2,
    href: '/login/institutional',
    color: 'bg-emerald-500',
    bgLight: 'bg-emerald-50',
    features: ['Apply for accreditation', 'Submit faculty details', 'Upload facility documents', 'Track application status']
  },
  {
    id: 'curriculum',
    title: 'Curriculum Submission',
    description: 'For institutions submitting training curricula for approval',
    icon: BookOpen,
    href: '/login/curriculum',
    color: 'bg-purple-500',
    bgLight: 'bg-purple-50',
    features: ['Submit curriculum for review', 'Upload learning modules', 'Track approval status', 'Receive feedback']
  },
  {
    id: 'examcenter',
    title: 'Examination Center',
    description: 'For accredited centers conducting IMCAC examinations',
    icon: MapPin,
    href: '/login/examination-center',
    color: 'bg-amber-500',
    bgLight: 'bg-amber-50',
    features: ['Apply as exam center', 'Manage exam schedules', 'Report examination conduct', 'View assigned candidates']
  }
];

export default function PortalSelectionPage() {
  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="portal-selection-page">
      {/* Header */}
      <section className="bg-[#1B2A4A] py-12">
        <div className="imcac-container text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_cbeam-portal/artifacts/2eoy3xqe_IMCAC%20%286%29.png"
            alt="IMCAC Logo"
            className="h-16 w-16 mx-auto mb-4 rounded-sm"
          />
          <h1 className="text-3xl font-bold text-white font-heading">IMCAC Portal Access</h1>
          <p className="text-slate-300 mt-2 max-w-xl mx-auto">
            Select your portal to access the appropriate dashboard. Each portal is designed for specific stakeholder roles.
          </p>
        </div>
      </section>

      {/* Portal Grid */}
      <section className="imcac-container py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {portals.map((portal) => {
            const IconComponent = portal.icon;
            return (
              <Link 
                key={portal.id} 
                to={portal.href}
                className="group"
                data-testid={`portal-${portal.id}`}
              >
                <Card className="h-full border border-slate-200 hover:border-[#C5A572] hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-sm ${portal.color} flex items-center justify-center mb-4`}>
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#1B2A4A] group-hover:text-[#C5A572] transition-colors">
                      {portal.title}
                    </h3>
                    <p className="text-sm text-slate-500 mt-2">{portal.description}</p>
                    
                    <div className={`mt-4 p-3 rounded-sm ${portal.bgLight}`}>
                      <p className="text-xs font-medium text-slate-600 mb-2">Features:</p>
                      <ul className="space-y-1">
                        {portal.features.map((feature, idx) => (
                          <li key={idx} className="text-xs text-slate-500 flex items-center gap-1">
                            <span className="w-1 h-1 rounded-full bg-slate-400"></span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-4 flex items-center text-sm text-[#C5A572] font-medium group-hover:translate-x-1 transition-transform">
                      Access Portal <ArrowRight className="h-4 w-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Info Box */}
        <div className="mt-12 bg-white border border-slate-200 rounded-sm p-6">
          <h3 className="font-semibold text-[#1B2A4A] mb-3">How to Get Access</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-slate-600">
            <div>
              <p className="font-medium text-slate-800 mb-2">For Candidates:</p>
              <p>Register directly through the Candidate Portal. You'll need your medical license details and professional information.</p>
            </div>
            <div>
              <p className="font-medium text-slate-800 mb-2">For Examiners:</p>
              <p>Examiner credentials are assigned by IMCAC administration. Contact <a href="mailto:admin@imcac.org" className="text-[#C5A572] hover:underline">admin@imcac.org</a> for examiner onboarding.</p>
            </div>
            <div>
              <p className="font-medium text-slate-800 mb-2">For Institutions & Exam Centers:</p>
              <p>Register through the respective portal and submit your application. Our team will review and provide access upon approval.</p>
            </div>
            <div>
              <p className="font-medium text-slate-800 mb-2">Need Help?</p>
              <p>Contact our support team at <a href="mailto:support@imcac.org" className="text-[#C5A572] hover:underline">support@imcac.org</a> or call +91-XXXX-XXXXXX during business hours.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
